import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * A ref that always holds the latest value, updated after commit — for callbacks read inside
 * long-lived subscriptions (chart events, timers) without re-subscribing on every render.
 */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Calls `refetch` just after `nextAt` (an ISO instant: the next bar close the server will
 * evaluate), with a small random jitter so a thousand browsers do not hit the same second
 * (§14.13). Never a short polling interval: the data only changes at a close.
 */
export function useRefetchAtBarClose(nextAt: string | null | undefined, refetch: () => void, fallbackMs = 5 * 60_000) {
  const cb = useLatest(refetch);
  useEffect(() => {
    const target = nextAt ? Date.parse(nextAt) : NaN;
    // Evaluation lands a few seconds after the close; wait for it, plus jitter.
    const delay = Number.isFinite(target) ? Math.max(5_000, target - Date.now() + 8_000 + Math.random() * 7_000) : fallbackMs;
    const id = window.setTimeout(() => cb.current(), Math.min(delay, 2 ** 31 - 1));
    return () => window.clearTimeout(id);
  }, [nextAt, fallbackMs, cb]);
}

/** `/` focuses the given input, unless the user is typing elsewhere. */
export function useSlashFocus(ref: RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      ref.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ref]);
}

/** Global key handler bound for the component's lifetime (←/→ through trades, Esc to close). */
export function useKeys(handlers: Record<string, (e: KeyboardEvent) => void>, enabled = true) {
  const ref = useLatest(handlers);
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
      const h = ref.current[e.key];
      if (h) h(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, ref]);
}

/** Whether a media query matches, kept current as the window resizes or the device turns. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatches(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return matches;
}

/** A value that updates after `ms` of quiet — for slider-driven requests. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** `value`, except that while `hold` is true it stays at what it was when the hold began. */
export function useHeldWhile<T>(value: T, hold: boolean): T {
  const [held, setHeld] = useState(value);
  if (!hold && held !== value) setHeld(value);
  return hold ? held : value;
}

/** Re-renders every `ms` (countdowns, "3 min ago"). */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/** A small persisted per-browser preference (view toggles); never for anything that must be shared. */
export function useLocalPref<T extends string>(key: string, initial: T, allowed: readonly T[]): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(`sixone8.${key}`);
      if (raw && (allowed as readonly string[]).includes(raw)) return raw as T;
    } catch {
      // Storage may be blocked.
    }
    return initial;
  });
  const set = (next: T) => {
    setV(next);
    try {
      localStorage.setItem(`sixone8.${key}`, next);
    } catch {
      // Non-fatal.
    }
  };
  return [v, set];
}
